import React, { useState, useEffect, useRef } from 'react';
import LoadingAnimation from './LoadingAnimation';

import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { CheckCircle2, XCircle, ArrowRight, ArrowLeft, Sparkles, AlertTriangle } from 'lucide-react';
import MultipleChoice from './quiz/MultipleChoice';
import TrueFalse from './quiz/TrueFalse';
import FillInBlank from './quiz/FillInBlank';
import Categorize from './quiz/Categorize';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SECURITY FIX (Simby-3): escapar HTML antes das substituições de markdown.
// O texto vem de conteúdo do banco e de respostas da IA (influenciadas pelo input
// do usuário) — sem escape, era um vetor de XSS via dangerouslySetInnerHTML.
const escapeHtml = (text) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderMarkdown = (text) => {
  if (!text) return '';
  return escapeHtml(text)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/---+/g, '<hr class="my-2 border-gray-300"/>')
    .replace(/\n/g, '<br/>');
};

const QuizLesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [progressSaved, setProgressSaved] = useState(false);
  const [islandId, setIslandId] = useState(1);
  const [resourcesEarned, setResourcesEarned] = useState(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [gamificationEnabled, setGamificationEnabled] = useState(false); // flag Configurações
  const [questions, setQuestions] = useState([]);
  const [lesson, setLesson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nextLesson, setNextLesson] = useState(null);

  // Use ref instead of state to prevent re-render loops
  const previousLessonIdRef = useRef(null);

  // AI Feedback state ("Entender melhor" button)
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [aiFeedback, setAiFeedback] = useState(null);
  const [showAiFeedback, setShowAiFeedback] = useState(false);

  // Wildcard fill_blank state
  const [wildcardResult, setWildcardResult] = useState(null);
  const [isEvaluatingWildcard, setIsEvaluatingWildcard] = useState(false);

  // Eco advance sharing state
  const [ecoShared, setEcoShared] = useState(false);
  const [sharingEco, setSharingEco] = useState(false);

  // Error report state (available to all users)
  const [showErrorForm, setShowErrorForm] = useState(false);
  const [errorDescription, setErrorDescription] = useState('');
  const [errorReported, setErrorReported] = useState(false);
  const [isReportingError, setIsReportingError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/public`)
      .then(r => setGamificationEnabled(!!r.data?.gamification_enabled))
      .catch(() => {});
  }, []);

  // Reset state when lesson changes - using ref to prevent reset during re-renders
  useEffect(() => {
    if (lessonId !== previousLessonIdRef.current) {
      previousLessonIdRef.current = lessonId;
      setQuizComplete(false);
      setProgressSaved(false);
      setCurrentQuestion(0);
      setAnswers({});
      setShowResult(false);
      setScore(0);
      setShowExplanation(false);
      setIsLoading(true);
      setNextLesson(null); // Reset next lesson so it gets fetched again
      fetchQuizData();
    }
  }, [lessonId]);

  const fetchQuizData = async () => {
    try {
      // Fetch questions by module_id and the module data
      const [questionsRes, moduleRes] = await Promise.all([
        axios.get(`${API}/questions?module_id=${lessonId}`, { withCredentials: true }),
        axios.get(`${API}/modules/${lessonId}`, { withCredentials: true })
      ]);

      const currentModule = moduleRes.data;
      setLesson(currentModule);
      setQuestions(questionsRes.data.sort((a, b) => a.order - b.order));

      // Fetch parent lesson to get island_id
      if (currentModule.lesson_id) {
        try {
          const lessonRes = await axios.get(`${API}/lessons/${currentModule.lesson_id}`, { withCredentials: true });
          setIslandId(lessonRes.data.island_id || 1);
        } catch { /* keep default */ }
      }

    } catch (error) {
      console.error('Error fetching quiz data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch next module when quiz is complete
  const fetchNextLesson = async () => {
    if (!lesson) return;

    try {
      const allModulesRes = await axios.get(
        `${API}/modules?lesson_id=${lesson.lesson_id}`,
        { withCredentials: true }
      );
      const lessonModules = allModulesRes.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      const currentIndex = lessonModules.findIndex(m => m.module_id === lessonId);

      if (currentIndex !== -1 && currentIndex < lessonModules.length - 1) {
        setNextLesson(lessonModules[currentIndex + 1]);
      } else {
        // Last module in the lesson - signal to go back to lesson list
        setNextLesson({ isLastModule: true, lesson_id: lesson.lesson_id });
      }
    } catch (error) {
      console.error('Error fetching next module:', error);
    }
  };

  // Fetch next lesson when quiz completes
  useEffect(() => {
    if (quizComplete && !nextLesson) {
      fetchNextLesson();
    }
  }, [quizComplete]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <div className="text-center">
          <LoadingAnimation size="md" className="mx-auto mb-4" />
          <p className="text-gray-600">Carregando quiz...</p>
        </div>
      </div>
    );
  }

  if (!lesson || questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-xl text-gray-600">Nenhuma questão disponível para esta lição</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Voltar ao Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  // Safety check - ensure currentQuestion is within bounds
  const safeCurrentQuestion = Math.min(currentQuestion, questions.length - 1);
  const question = questions[safeCurrentQuestion];
  const progress = ((safeCurrentQuestion + 1) / questions.length) * 100;
  const currentAnswer = answers[safeCurrentQuestion];

  // If question is still undefined, show error
  if (!question) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-xl text-gray-600">Erro ao carregar questão</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Voltar ao Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const setCurrentAnswer = (answer) => {
    setAnswers({
      ...answers,
      [safeCurrentQuestion]: answer
    });
  };

  const checkAnswer = (answerOverride) => {
    const answer = answerOverride !== undefined ? answerOverride : answers[safeCurrentQuestion];

    switch (question.type) {
      case 'multiple_choice':
        // Support both single value and array of correct answers
        const correctAnswers = Array.isArray(question.correct_answer)
          ? question.correct_answer
          : [question.correct_answer];
        return correctAnswers.includes(answer);

      case 'true_false':
        return answer === question.correct_answer;

      case 'fill_blank':
        if (question.wildcard) return wildcardResult?.is_correct ?? false;
        if (!answer) return false;
        const normalized = answer.toLowerCase().trim();
        // Fallback: if missing_words is empty, try correct_answer as string
        const wordsToCheck = question.missing_words?.length
          ? question.missing_words
          : question.correct_answer != null ? [String(question.correct_answer)] : [];
        return wordsToCheck.some(word => word.toLowerCase().trim() === normalized);

      case 'open_ended':
        return wildcardResult?.is_correct ?? false;

      case 'categorize': {
        const catItems = question.items_to_categorize;
        if (!catItems || catItems.length === 0) return true;
        if (!answer) return false;
        return catItems.every(item => answer[item.text] === item.category);
      }

      default:
        return false;
    }
  };

  const needsManualSubmit = () => {
    return question.type === 'categorize' ||
      question.type === 'open_ended' ||
      question.type === 'fill_blank';
  };

  // Auto-submit for multiple_choice and true_false (no async eval needed)
  const handleAutoSubmit = (answer) => {
    if (showResult) return;
    setAnswers(prev => ({ ...prev, [safeCurrentQuestion]: answer }));
    const isCorrect = checkAnswer(answer);
    setShowResult(true);
    setShowExplanation(true);
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
  };

  const handleSubmit = async () => {
    const answer = answers[safeCurrentQuestion];

    // Check if answer exists (handle 0 index correctly)
    if (answer === undefined || answer === null) {
      if (question.type === 'multiple_choice' || question.type === 'true_false') {
        return;
      }
    }

    // Wildcard fill_blank or open_ended: evaluate with AI before showing result
    if (question.type === 'open_ended' || (question.type === 'fill_blank' && question.wildcard)) {
      if (!answer || !answer.trim()) return;
      setIsEvaluatingWildcard(true);
      try {
        const res = await axios.post(
          `${API}/simulator/quiz/evaluate-wildcard`,
          {
            question_text: question.question,
            user_answer: answer,
            evaluation_criteria: question.evaluation_criteria || '',
            explanation: question.explanation || ''
          },
          { withCredentials: true }
        );
        setWildcardResult(res.data);
        setShowResult(true);
        setShowExplanation(true);
        if (res.data.is_correct) {
          setScore(prev => prev + 1);
        }
      } catch (e) {
        console.error('Wildcard evaluation error:', e);
        // Show result as incorrect so quiz doesn't freeze on API failure
        setWildcardResult({ is_correct: false, score: 0, feedback: 'Não foi possível avaliar a resposta no momento. Tente novamente.' });
        setShowResult(true);
        setShowExplanation(true);
      } finally {
        setIsEvaluatingWildcard(false);
      }
      return;
    }

    const isCorrect = checkAnswer();

    setShowResult(true);
    setShowExplanation(true);

    if (isCorrect) {
      setScore(prev => prev + 1);
    }
  };

  const getScorePercentage = () => {
    // Score is already updated by handleSubmit before this is called
    // No need to add extra point here
    return Math.round((score / questions.length) * 100);
  };

  const handleNext = async () => {
    // Prevent double click or calling after completion
    if (quizComplete || isSaving) {
      return;
    }

    // Admin skipping an errored question: don't count as correct/incorrect
    if (errorReported && !showResult) {
      if (safeCurrentQuestion < questions.length - 1) {
        setCurrentQuestion(safeCurrentQuestion + 1);
        setShowResult(false);
        setShowExplanation(false);
        setAiFeedback(null);
        setShowAiFeedback(false);
        setWildcardResult(null);
        setErrorReported(false);
        setShowErrorForm(false);
        setErrorDescription('');
      } else {
        setQuizComplete(true);
        await saveProgress(getScorePercentage() >= 70);
      }
      return;
    }

    if (safeCurrentQuestion < questions.length - 1) {
      setCurrentQuestion(safeCurrentQuestion + 1);
      setShowResult(false);
      setShowExplanation(false);
      // Reset AI feedback and wildcard for next question
      setAiFeedback(null);
      setShowAiFeedback(false);
      setWildcardResult(null);
      // Reset admin error state
      setErrorReported(false);
      setShowErrorForm(false);
      setErrorDescription('');
    } else {
      // Calculate final results BEFORE setting complete
      const percentage = getScorePercentage();
      const passed = percentage >= 70;

      // Quiz finished - show completion screen first
      setQuizComplete(true);

      // Save progress in background
      await saveProgress(passed);
    }
  };

  const saveProgress = async (passed) => {
    if (isSaving || progressSaved) {
      return;
    }
    setIsSaving(true);

    try {
      const percentage = getScorePercentage();

      // Save lesson completion with new gamification payload
      const completionRes = await axios.post(
        `${API}/auth/complete-lesson`,
        {
          lesson_id: lessonId,
          action_type: 'module',
          island_id: islandId,
          score: percentage
        },
        { withCredentials: true }
      );
      if (completionRes.data?.resources_earned &&
          completionRes.data.message !== "Lesson already completed") {
        setResourcesEarned(completionRes.data.resources_earned);
        setPointsEarned(completionRes.data.points_earned ?? 0);
      }

      // Also save to progress tracking for evolution graphs
      try {
        await axios.post(
          `${API}/progress/quiz`,
          {
            lesson_id: lessonId,
            score: percentage,
            total_questions: questions.length,
            correct_answers: score,
            skills_practiced: [], // TODO: Map questions to skills
            time_taken: null
          },
          { withCredentials: true }
        );
      } catch (progressError) {
        console.error('Error saving to progress tracking:', progressError);
        // Don't fail the whole operation if progress tracking fails
      }

      // Refresh user data and wait for it to complete before showing result
      await checkAuth();

      setProgressSaved(true);

    } catch (error) {
      console.error('Error saving progress:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetry = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setShowExplanation(false);
    setScore(0);
    setQuizComplete(false);
    setAiFeedback(null);
    setShowAiFeedback(false);
    setWildcardResult(null);
    setIsEvaluatingWildcard(false);
    setErrorReported(false);
    setShowErrorForm(false);
    setErrorDescription('');
  };

  const handleShareEco = async () => {
    setSharingEco(true);
    try {
      const res = await axios.post(
        `${API}/eco/advance`,
        { lesson_id: lessonId, island_id: islandId, lesson_title: lesson?.title || '' },
        { withCredentials: true }
      );
      if (res.data.echo) {
        setEcoShared(true);
        toast({ title: 'Eco compartilhado! 💧', description: res.data.echo.message_text });
      } else {
        toast({
          title: 'Você ainda não tem um círculo',
          description: 'Entre em um círculo para compartilhar seu progresso.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({ title: 'Não foi possível compartilhar', variant: 'destructive' });
    } finally {
      setSharingEco(false);
    }
  };

  // Report error on current question (all authenticated users)
  const handleReportError = async () => {
    if (isReportingError) return;
    setIsReportingError(true);
    try {
      await axios.post(
        `${API}/admin/report-exercise-error`,
        {
          question_id: question.question_id,
          module_id: question.module_id || lessonId,
          question_text: question.question,
          question_type: question.type,
          error_description: errorDescription.trim()
        },
        { withCredentials: true }
      );
      setErrorReported(true);
      setShowErrorForm(false);
    } catch (e) {
      console.error('Error reporting exercise error:', e);
    } finally {
      setIsReportingError(false);
    }
  };

  // Get AI feedback for "Entender melhor" button
  const getAIFeedback = async () => {
    if (isLoadingFeedback || showAiFeedback) return;

    setIsLoadingFeedback(true);
    try {
      const response = await axios.post(
        `${API}/simulator/quiz/feedback`,
        {
          question_id: question.question_id,
          question_text: question.question,
          question_type: question.type,
          options: question.options || null,
          user_answer: String(answers[safeCurrentQuestion]),
          correct_answer: String(question.correct_answer),
          is_correct: checkAnswer(),
          original_explanation: question.explanation || '',
          lesson_context: lesson?.title || null
        },
        { withCredentials: true }
      );

      setAiFeedback(response.data.feedback);
      setShowAiFeedback(true);
    } catch (error) {
      console.error('Error getting AI feedback:', error);
      // Show a fallback message on error
      setAiFeedback('Desculpe, não foi possível gerar uma explicação adicional no momento. Tente novamente mais tarde.');
      setShowAiFeedback(true);
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const canSubmit = () => {
    const answer = answers[safeCurrentQuestion];

    // Handle index 0 correctly - check for undefined/null instead of falsy
    if (answer === undefined || answer === null) return false;

    if (question.type === 'categorize') {
      const catItems = question.items_to_categorize;
      if (!catItems || catItems.length === 0) return true;
      return catItems.every(item => answer[item.text] !== null);
    }

    return true;
  };

  if (quizComplete) {
    const percentage = getScorePercentage();
    const passed = percentage >= 70;

    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full p-8 text-center">
          <div className="text-8xl mb-6">
            {passed ? '🎉' : '🦒'}
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {passed ? 'Parabéns!' : 'Continue Tentando!'}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {passed
              ? 'Você completou a lição com sucesso!'
              : 'Você pode tentar novamente para melhorar sua pontuação.'}
          </p>

          <div className="bg-purple-50 rounded-2xl p-6 mb-8">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-3xl font-bold text-purple-600">{score}/{questions.length}</div>
                <div className="text-sm text-gray-600">Acertos</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600">{percentage}%</div>
                <div className="text-sm text-gray-600">Precisão</div>
              </div>
            </div>
            {gamificationEnabled && passed && resourcesEarned && (
              <div className="border-t border-purple-200 pt-3 text-center">
                <p className="text-sm font-semibold text-purple-700 mb-1">Recursos ganhos</p>
                <div className="flex justify-center gap-4 text-sm">
                  {resourcesEarned.watering_cans > 0 && <span>🪣 +{resourcesEarned.watering_cans}</span>}
                  {resourcesEarned.waterings > 0 && <span>💧 +{resourcesEarned.waterings}</span>}
                  {resourcesEarned.fertilizers > 0 && <span>🌿 +{resourcesEarned.fertilizers}</span>}
                  {pointsEarned > 0 && <span>🌱 +{pointsEarned}pt</span>}
                </div>
              </div>
            )}
          </div>

          {/* Share eco button */}
          {passed && (
            <div className="mb-6">
              {ecoShared ? (
                <p className="text-sm text-purple-600 font-semibold text-center">✓ Progresso compartilhado no círculo!</p>
              ) : (
                <button
                  onClick={handleShareEco}
                  disabled={sharingEco}
                  className="w-full py-3 rounded-xl border-2 border-purple-300 text-purple-700 text-sm font-semibold hover:bg-purple-50 transition-all disabled:opacity-60"
                >
                  {sharingEco ? 'Compartilhando...' : '💧 Compartilhar progresso no círculo'}
                </button>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {passed ? (
              nextLesson ? (
                <>
                  {nextLesson.isLastModule ? (
                    <>
                      <Button
                        onClick={() => navigate(nextLesson.lesson_id ? `/lesson-modules/${nextLesson.lesson_id}` : '/dashboard')}
                        size="lg"
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Voltar à Lição
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      <Button
                        onClick={() => navigate('/dashboard')}
                        size="lg"
                        variant="outline"
                        className="border-2 border-gray-300"
                      >
                        Ir ao Dashboard
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => {
                          const nextType = nextLesson.type === 'quiz' ? 'quiz' :
                            nextLesson.type === 'video' ? 'lesson' : 'simulator';
                          navigate(`/${nextType}/${nextLesson.module_id}`);
                        }}
                        size="lg"
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Próximo: {nextLesson.title}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      <Button
                        onClick={() => navigate('/dashboard')}
                        size="lg"
                        variant="outline"
                        className="border-2 border-gray-300"
                      >
                        Voltar ao Dashboard
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    size="lg"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Parabéns! Você completou todas as ilhas!
                  </Button>
                  <p className="text-center text-sm text-gray-600 w-full">
                    Você completou todo o conteúdo disponível. Continue praticando nos simuladores!
                  </p>
                </>
              )
            ) : (
              <>
                <Button
                  onClick={handleRetry}
                  size="lg"
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Tentar Novamente
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300"
                >
                  Voltar ao Dashboard
                </Button>
              </>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-40 md:pb-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div></div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{lesson.title}</h1>
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Questão {safeCurrentQuestion + 1} de {questions.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Video (if available) */}
        {lesson.videoUrl && safeCurrentQuestion === 0 && (
          <Card className="mb-6 overflow-hidden">
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={lesson.videoUrl}
                title="Lesson Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </Card>
        )}

        {/* Question */}
        <Card className="p-8 mb-6">
          <div className="mb-2 text-xs font-semibold text-purple-600 uppercase">
            {question.type === 'multiple_choice' && 'Múltipla Escolha'}
            {question.type === 'true_false' && 'Verdadeiro ou Falso'}
            {question.type === 'fill_blank' && 'Complete a Frase'}
            {question.type === 'open_ended' && 'Resposta Livre'}
            {question.type === 'categorize' && 'Categorize'}
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {question.type !== 'fill_blank' && question.type !== 'open_ended' && question.type !== 'categorize' && question.question}
          </h2>

          {/* Render appropriate question type */}
          {question.type === 'multiple_choice' && (
            <MultipleChoice
              question={question}
              selectedAnswer={currentAnswer}
              onSelectAnswer={setCurrentAnswer}
              onSelectAndSubmit={handleAutoSubmit}
              showResult={showResult}
            />
          )}

          {question.type === 'true_false' && (
            <TrueFalse
              question={question}
              selectedAnswer={currentAnswer}
              onSelectAnswer={setCurrentAnswer}
              onSelectAndSubmit={handleAutoSubmit}
              showResult={showResult}
            />
          )}

          {(question.type === 'fill_blank' || question.type === 'open_ended') && (
            <FillInBlank
              key={safeCurrentQuestion}
              question={{
                ...question,
                wildcard: question.type === 'open_ended' ? true : question.wildcard
              }}
              answer={currentAnswer}
              onAnswer={setCurrentAnswer}
              showResult={showResult}
              wildcardResult={wildcardResult}
              isEvaluatingWildcard={isEvaluatingWildcard}
              onRetry={() => {
                setWildcardResult(null);
                setShowResult(false);
                setShowExplanation(false);
                setAiFeedback(null);
                setShowAiFeedback(false);
              }}
            />
          )}

          {question.type === 'categorize' && (
            <Categorize
              key={question.id || safeCurrentQuestion}
              question={question}
              answer={currentAnswer}
              onAnswer={setCurrentAnswer}
              showResult={showResult}
            />
          )}
        </Card>

        {/* Result + Next (compact, inline) */}
        {showExplanation && (
          <div className={`p-4 rounded-xl border-2 mb-6 ${checkAnswer()
            ? 'bg-green-50 border-green-300'
            : 'bg-red-50 border-red-300'
          }`}>
            <div className="space-y-3">
              <span className="text-lg font-semibold block">
                {checkAnswer() ? '✅ Correto!' : '❌ Incorreto'}
              </span>

            {(question.explanation || !showAiFeedback) && (
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800 select-none">
                  Ver explicação
                </summary>
                <div className="mt-2 pt-2 border-t border-gray-200">
                  {question.explanation && (
                    <p className="text-gray-700 text-sm mb-3"
                       dangerouslySetInnerHTML={{ __html: renderMarkdown(question.explanation) }} />
                  )}
                  {showAiFeedback && aiFeedback ? (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span className="font-semibold text-purple-800 text-sm">Simby explica:</span>
                      </div>
                      <p className="text-gray-700 text-sm"
                         dangerouslySetInnerHTML={{ __html: renderMarkdown(aiFeedback) }} />
                    </div>
                  ) : (
                    <Button
                      onClick={getAIFeedback}
                      disabled={isLoadingFeedback}
                      variant="outline"
                      size="sm"
                      className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    >
                      {isLoadingFeedback ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                          Carregando...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Entender melhor
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </details>
            )}
            <Button
              onClick={handleNext}
              size="lg"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white mt-1"
            >
              {safeCurrentQuestion < questions.length - 1 ? 'Próxima' : 'Finalizar'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            </div>
          </div>
        )}

        {/* Error report form — available to all users */}
        {showErrorForm && (
          <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-xl">
            <p className="text-sm font-semibold text-yellow-800 mb-1">Reportar problema na questão</p>
            <div className="mb-2 p-2 bg-yellow-100 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-700 font-medium uppercase tracking-wide mb-0.5">Questão atual:</p>
              <p className="text-sm text-yellow-900 line-clamp-3">{question.question}</p>
            </div>
            <p className="text-xs text-yellow-700 mb-2">Gabarito errado, enunciado confuso, opção duplicada... qualquer coisa que não parece certa.</p>
            <textarea
              className="w-full border border-yellow-300 rounded-lg p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400"
              rows={3}
              placeholder="Descreva o problema (opcional)"
              value={errorDescription}
              onChange={e => setErrorDescription(e.target.value)}
            />
            <div className="flex gap-2 mt-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowErrorForm(false); setErrorDescription(''); }}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
                onClick={handleReportError}
                disabled={isReportingError}
              >
                {isReportingError ? 'Enviando...' : 'Enviar'}
              </Button>
            </div>
          </div>
        )}

        {/* Error reported confirmation */}
        {errorReported && !showResult && (
          <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-xl flex items-center justify-between gap-4">
            <span className="flex items-center gap-2 text-yellow-800 font-semibold text-sm">
              <AlertTriangle className="w-4 h-4" />
              Obrigada — vamos verificar isso.
            </span>
            <Button
              onClick={handleNext}
              size="lg"
              className="bg-purple-600 hover:bg-purple-700 shrink-0"
            >
              {safeCurrentQuestion < questions.length - 1 ? 'Próxima' : 'Finalizar'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Actions — only show Verificar for types needing manual submit */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
          {!showResult && (
            <div className="flex gap-2 items-center order-1 sm:order-2">
              {!errorReported && (
                <Button
                  variant="outline"
                  size="lg"
                  className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                  onClick={() => setShowErrorForm(f => !f)}
                >
                  <AlertTriangle className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Reportar problema</span>
                </Button>
              )}

              {needsManualSubmit() && (
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit() || isEvaluatingWildcard}
                  size="lg"
                  className="flex-1 sm:flex-none bg-purple-600 hover:bg-purple-700"
                >
                  {isEvaluatingWildcard ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Avaliando...
                    </>
                  ) : 'Verificar Resposta'}
                </Button>
              )}
            </div>
          )}

          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            size="lg"
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Sair
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuizLesson;


